import React, { useEffect, useState } from 'react'
import Layout from "../layout/Layout";
import { Col, Row } from 'react-bootstrap';
import InfoCard from '../components/Dashboard/InfoCard';
import PostedActivity from '../components/Dashboard/PostedActivity';
import { getDashboard } from '../controller';
import { requestHandler } from '../helper';
import Loader from '../components/Loader';
import UserChart from '../components/Dashboard/UserChart';
import EventChart from '../components/Dashboard/EventChart';
import { useDashboard } from '../context/DashboardContext';

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [userChartData, setUserChartData] = useState({});
  const [eventChartData, setEventChartData] = useState({});
  const { selectedPeriod, selectedCustomRange, customRangeApplied, setCustomRangeApplied } = useDashboard();

  const currentYear = new Date().getFullYear();
  const [userChartYear, setUserChartYear] = useState(currentYear);
  const [eventChartYear, setEventChartYear] = useState(currentYear);

  // Fetch data when period or custom range changes (for InfoCard and PostedActivity)
  useEffect(() => {
    // Clear custom range when period changes (unless it's custom)
    if (selectedPeriod !== 'custom') {
      setCustomRangeApplied(false);
      // Clear data when switching away from custom range
      if (Object.keys(data).length > 0) {
        setData({});
      }
    }
    getData();
  }, [selectedPeriod, customRangeApplied]);

  // Fetch user chart data when year changes
  useEffect(() => {
    getUserChartData();
    getEventChartData();
  }, [userChartYear, eventChartYear, selectedPeriod, customRangeApplied]);

  const getData = async () => {
    let apiParams = selectedPeriod;
 
    if (selectedPeriod === 'custom') {
      if (!selectedCustomRange.start || !selectedCustomRange.end) {
        console.warn('Custom period requires both startDate and endDate');
        setData({}); // Clear data if custom range is incomplete
        return; // Don't make API call if dates are missing
      }
      
      apiParams = {
        period: 'custom',
        startDate: selectedCustomRange.start,
        endDate: selectedCustomRange.end
      };
    }
    
    await requestHandler(
      async () => await getDashboard(apiParams),
      setLoading,
      (res) => {
        setData(res.data);
        // Reset customRangeApplied after successful API call
        if (selectedPeriod === 'custom') {
          setCustomRangeApplied(false);
        }
      }
    );
  }

  const getUserChartData = async () => {
    const chartPeriod = userChartYear === currentYear ? 'currentYear' : 'previousYear';
    
    let apiParams = {
    //   period: selectedPeriod,
    //   year: userChartYear,
      chartPeriod: chartPeriod
    };
 
    if (selectedPeriod === 'custom') {
      if (!selectedCustomRange.start || !selectedCustomRange.end) {
        return;
      }
      apiParams = {
    //     period: 'custom',
    //     startDate: selectedCustomRange.start,
    //     endDate: selectedCustomRange.end,
    //     year: userChartYear,
        chartPeriod: chartPeriod
      };
    }
    
    await requestHandler(
      async () => await getDashboard(apiParams),
      setLoading,
      (res) => {
        setUserChartData(res.data);
      }
    );
  }

  const getEventChartData = async () => {
    const chartPeriod = eventChartYear === currentYear ? 'currentYear' : 'previousYear';
    
    let apiParams = {
    //   period: selectedPeriod,
    //   year: eventChartYear,
      chartPeriod: chartPeriod
    };
 
    if (selectedPeriod === 'custom') {
      if (!selectedCustomRange.start || !selectedCustomRange.end) {
        return;
      }
      apiParams = {
    //     period: 'custom',
    //     startDate: selectedCustomRange.start,
    //     endDate: selectedCustomRange.end,
    //     year: eventChartYear,
        chartPeriod: chartPeriod
      };
    }
    
    await requestHandler(
      async () => await getDashboard(apiParams),
      setLoading,
      (res) => {
        setEventChartData(res.data);
      }
    );
  }

  // Process user chart data based on API response
  const getUserChartDataProcessed = () => {
    const chartData = userChartData.userChartData || [];
    
    const userCategories = chartData.map(item => item.period);
    const userSeries = [
      {
        name: "Individuals",
        data: chartData.map(item => item.individuals || 0),
        show: true
      },
      {
        name: "Organizations",
        data: chartData.map(item => item.organizations || 0),
        show: true
      }
    ];

    return {
      userCategories: userCategories.length > 0 ? userCategories : ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      userSeries
    };
  };

  // Process event chart data based on API response
  const getEventChartDataProcessed = () => {
    const chartData = eventChartData.eventChartData || [];
    
    const eventCategories = chartData.map(item => item.period);
    const eventSeries = [
      {
        name: "Individuals",
        data: chartData.map(item => item.individualEvents || 0),
        show: true
      },
      {
        name: "Organizations",
        data: chartData.map(item => item.organizationEvents || 0),
        show: true
      }
    ];

    return {
      eventCategories: eventCategories.length > 0 ? eventCategories : ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      eventSeries
    };
  };

  const processedUserChartData = getUserChartDataProcessed();
  const processedEventChartData = getEventChartDataProcessed();

  return (
    <>
      {loading && <Loader />}
      <Layout>
        <Row className="g-4">
          <Col xs={12}>
            <InfoCard data={data} />
          </Col>
            <Col xl={6} sm={6}>
                <UserChart
                    title="Users"
                    categories={processedUserChartData.userCategories}
                    series={processedUserChartData.userSeries}
                    selectedYear={userChartYear}
                    onYearChange={setUserChartYear}
                />
            </Col>
            <Col xl={6} sm={6}>
                <EventChart
                    title="Events Created"
                    categories={processedEventChartData.eventCategories}
                    series={processedEventChartData.eventSeries}
                    selectedYear={eventChartYear}
                    onYearChange={setEventChartYear}
                />
            </Col>
          <Col xs={12}>
            <PostedActivity latestActivity={data.latestActivity} latestEvent={data.latestEvent} />
          </Col>
        </Row>
      </Layout>
    </>
  )
}

export default Dashboard